import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "@/lib/api";
import { BookOpen, Loader2, Search, X, ChevronRight } from "lucide-react";

// --- Helpers ---

// Build a GitHub-ish slug from a heading string so that anchor links match
// react-markdown's rendered <h{n} id="..."> on screen.
function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// Parse markdown and return a flat list of {level, text, slug} entries for
// H1/H2/H3 only. Skips headings inside fenced code blocks.
function extractToc(md) {
  if (!md) return [];
  const lines = md.split("\n");
  const out = [];
  let inFence = false;
  const seen = new Map();
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("```")) { inFence = !inFence; continue; }
    if (inFence) continue;
    const m = /^(#{1,3})\s+(.+?)\s*#*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/`/g, "").trim();
    let slug = slugify(text);
    const n = seen.get(slug) || 0;
    seen.set(slug, n + 1);
    if (n > 0) slug = `${slug}-${n}`;
    out.push({ level, text, slug });
  }
  return out;
}

export default function Help() {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [activeSlug, setActiveSlug] = useState("");
  const contentRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/help/guide");
      setMarkdown(data.markdown || "");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toc = useMemo(() => extractToc(markdown), [markdown]);

  const filteredToc = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return toc;
    return toc.filter((e) => e.text.toLowerCase().includes(q));
  }, [toc, query]);

  // Track active heading while scrolling (IntersectionObserver on visible headings)
  useEffect(() => {
    if (!contentRef.current || toc.length === 0) return;
    const root = contentRef.current;
    const headings = Array.from(root.querySelectorAll("h1, h2, h3"));
    if (headings.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActiveSlug(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: [0, 1] }
    );
    headings.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, [markdown, toc]);

  const jumpTo = (slug) => {
    const el = document.getElementById(slug);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSlug(slug);
    // Update URL hash without reload
    window.history.replaceState(null, "", `#${slug}`);
  };

  // On first load, if URL has a hash, try to scroll to it after markdown renders.
  useEffect(() => {
    if (!markdown) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => jumpTo(hash), 80);
    return () => clearTimeout(t);
  }, [markdown]);

  return (
    <div className="space-y-4" data-testid="help-page">
      <div className="flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-[#66cc33]" />
        <div>
          <h1 className="text-2xl font-bold text-[#1a1c19]">User Guide</h1>
          <p className="text-sm text-[#5c665a]">
            Everything the platform does — Admin, Finance, Referrer, and Referee flows.
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 rounded-lg border border-[#e2e8e2] bg-white p-6 text-sm text-[#5c665a]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading guide…
        </div>
      )}

      {err && !loading && (
        <div className="rounded-lg border border-[#fecaca] bg-[#fff0f5] p-4 text-sm text-[#c01974]" data-testid="help-error">
          {err}
        </div>
      )}

      {!loading && !err && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px,1fr]">
          {/* TOC sidebar */}
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <div className="rounded-lg border border-[#e2e8e2] bg-white p-3">
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3a6]" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the guide…"
                  data-testid="help-toc-search"
                  className="w-full rounded-md border border-[#e2e8e2] bg-[#fafcfa] py-2 pl-8 pr-8 text-sm outline-none focus:border-[#66cc33]"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9ca3a6] hover:text-[#1a1c19]"
                    data-testid="help-toc-search-clear"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-[#9ca3a6]">
                {query ? `${filteredToc.length} of ${toc.length} sections` : `${toc.length} sections`}
              </div>
              <nav
                className="max-h-[calc(100vh-220px)] overflow-y-auto pr-1"
                data-testid="help-toc-list"
              >
                {filteredToc.length === 0 && (
                  <div className="px-2 py-4 text-xs text-[#9ca3a6]">No matches.</div>
                )}
                {filteredToc.map((e) => {
                  const isActive = activeSlug === e.slug;
                  const pad = e.level === 1 ? "pl-2" : e.level === 2 ? "pl-5" : "pl-8";
                  return (
                    <button
                      key={e.slug}
                      type="button"
                      onClick={() => jumpTo(e.slug)}
                      data-testid={`help-toc-${e.slug}`}
                      className={`group flex w-full items-start gap-1 rounded-md py-1.5 pr-2 text-left text-[13px] leading-snug transition-colors ${pad} ${
                        isActive
                          ? "bg-[#eaf8de] font-semibold text-[#3f7c21]"
                          : "text-[#5c665a] hover:bg-[#fafcfa] hover:text-[#1a1c19]"
                      }`}
                    >
                      {e.level === 1 ? (
                        <ChevronRight className={`mt-0.5 h-3 w-3 shrink-0 ${isActive ? "text-[#66cc33]" : "text-[#c7d0c6]"}`} />
                      ) : (
                        <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#c7d0c6] group-hover:bg-[#66cc33]" />
                      )}
                      <span className="min-w-0 flex-1 break-words">{e.text}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article
            ref={contentRef}
            data-testid="help-content"
            className="help-prose min-w-0 rounded-lg border border-[#e2e8e2] bg-white p-6 sm:p-8"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, children, ...props }) => {
                  const slug = slugify(String(children));
                  return <h1 id={slug} {...props}>{children}</h1>;
                },
                h2: ({ node, children, ...props }) => {
                  const slug = slugify(String(children));
                  return <h2 id={slug} {...props}>{children}</h2>;
                },
                h3: ({ node, children, ...props }) => {
                  const slug = slugify(String(children));
                  return <h3 id={slug} {...props}>{children}</h3>;
                },
                a: ({ node, href, children, ...props }) => {
                  // Internal anchor → jump; external → new tab
                  if (href && href.startsWith("#")) {
                    return (
                      <a
                        href={href}
                        onClick={(e) => { e.preventDefault(); jumpTo(href.slice(1)); }}
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noreferrer" {...props}>
                      {children}
                    </a>
                  );
                },
                table: ({ node, children, ...props }) => (
                  <div className="my-4 overflow-x-auto">
                    <table {...props}>{children}</table>
                  </div>
                ),
              }}
            >
              {markdown}
            </ReactMarkdown>
          </article>
        </div>
      )}
    </div>
  );
}
